import { Colors, Icon, MultilineInput, TextInput } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import { computeNextRatingScaleBounds } from './ratingScale.utils';

import { FormField } from '@/components/FormField/FormField';
import { useAmelioTranslations } from '@/hooks';

type RatingScaleBound = 'from' | 'to';

interface RatingScaleFormatChange {
    fromValue: string;
    toValue: string;
    fromText?: string;
    toText?: string;
}

interface RatingScaleFormatProps {
    fromValue: string;
    toValue: string;
    fromText?: string;
    toText?: string;
    onChange: (values: RatingScaleFormatChange) => void;
}

export const RatingScaleFormat = ({
    fromValue,
    toValue,
    fromText,
    toText,
    onChange,
}: RatingScaleFormatProps) => {
    const [fromDraft, setFromDraft] = useState(fromValue);
    const [toDraft, setToDraft] = useState(toValue);

    const { t } = useAmelioTranslations();

    useEffect(() => {
        setFromDraft(fromValue);
    }, [fromValue]);

    useEffect(() => {
        setToDraft(toValue);
    }, [toValue]);

    const handleBoundValueChange = ({
        inputValue,
        source,
    }: {
        inputValue: string;
        source: RatingScaleBound;
    }) => {
        if (inputValue === '') {
            if (source === 'from') {
                setFromDraft('');
            } else {
                setToDraft('');
            }
            return;
        }

        if (Number.isNaN(Number(inputValue))) {
            return;
        }

        const nextBounds = computeNextRatingScaleBounds({
            inputValue,
            source,
            fromValue,
            toValue,
        });

        setFromDraft(nextBounds.fromValue);
        setToDraft(nextBounds.toValue);

        onChange({ ...nextBounds, fromText, toText });
    };

    const handleBoundBlur = (source: RatingScaleBound) => {
        if (source === 'from' && fromDraft === '') {
            setFromDraft(fromValue);
            return;
        }

        if (source === 'to' && toDraft === '') {
            setToDraft(toValue);
        }
    };

    return (
        <div className={'qf-row mt-4'}>
            <div className={'flex flex-1 flex-row items-start gap-2'}>
                <FormField
                    label={t('common.from')}
                    className={'max-w-25'}
                >
                    <TextInput
                        data-testid={'from-value-input'}
                        placeholder={'1'}
                        type={'number'}
                        value={fromDraft}
                        onChange={(value) =>
                            handleBoundValueChange({ inputValue: value, source: 'from' })
                        }
                        onBlur={() => handleBoundBlur('from')}
                    />
                </FormField>

                <div className={'flex-1'}>
                    <div
                        className={'h-8'}
                        aria-hidden={'true'}
                    />

                    <MultilineInput
                        dataTestId={'from-text-input'}
                        placeholder={t(
                            'evaluation360Questionnaire.questionForm.questions.common.stronglyDisagree',
                        )}
                        value={fromText}
                        onChange={(value) =>
                            onChange({
                                fromValue,
                                toValue,
                                fromText: value,
                                toText,
                            })
                        }
                        rows={1}
                        autoGrow={true}
                    />
                </div>
            </div>

            <div className={'rsf-arrow'}>
                <Icon
                    name={'arrow-arrow-right'}
                    size={'xl'}
                    color={Colors.BASE}
                />
            </div>

            <div className={'flex flex-1 flex-row items-start gap-2'}>
                <FormField
                    label={t('common.to')}
                    className={'max-w-25'}
                >
                    <TextInput
                        data-testid={'to-value-input'}
                        placeholder={'7'}
                        type={'number'}
                        value={toDraft}
                        onChange={(value) =>
                            handleBoundValueChange({ inputValue: value, source: 'to' })
                        }
                        onBlur={() => handleBoundBlur('to')}
                    />
                </FormField>

                <div className={'flex-1'}>
                    <div
                        className={'h-8'}
                        aria-hidden={'true'}
                    />

                    <MultilineInput
                        dataTestId={'to-text-input'}
                        placeholder={t(
                            'evaluation360Questionnaire.questionForm.questions.common.stronglyAgree',
                        )}
                        value={toText}
                        onChange={(value) =>
                            onChange({
                                fromValue,
                                toValue,
                                fromText,
                                toText: value,
                            })
                        }
                        rows={1}
                        autoGrow={true}
                    />
                </div>
            </div>
        </div>
    );
};
